import { type Table, flexRender } from "@tanstack/react-table";
import {
  EmptyState,
  ErrorState,
  LoadingState,
} from "@features/students/components/TableStates";
import { Text } from "@core/text";
import { TruncatedCell, createTruncatedCell } from "./TruncatedCell";

export { TruncatedCell, createTruncatedCell };

interface TableProps<T> {
  table: Table<T>;
  isLoading?: boolean;
  error?: unknown;
  isEmpty?: boolean;
  customEmptyState?: React.ReactNode;
  customLoadingState?: React.ReactNode;
  customErrorState?: React.ReactNode;
}

const DesktopTable = <T,>({ table }: { table: Table<T> }) => (
  <div className="max-h-96 hidden md:block overflow-x-hidden overflow-y-scroll bg-white dark:bg-gray-800 rounded-lg shadow max-w-full mt-6 border border-gray-200 dark:border-gray-700 [&::-webkit-scrollbar]:w-2 [&::-webkit-scrollbar-track]:bg-gray-100 [&::-webkit-scrollbar-track]:dark:bg-gray-700 [&::-webkit-scrollbar-thumb]:bg-gray-300 [&::-webkit-scrollbar-thumb]:dark:bg-gray-500 [&::-webkit-scrollbar-thumb]:rounded-full">
    <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
      <thead className="sticky top-0 bg-gray-50 dark:bg-gray-700 z-10">
        {table.getHeaderGroups().map((headerGroup) => (
          <tr key={headerGroup.id}>
            {headerGroup.headers.map((header) => (
              <th
                key={header.id}
                className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider cursor-pointer"
                onClick={header.column.getToggleSortingHandler()}
              >
                {flexRender(
                  header.column.columnDef.header,
                  header.getContext(),
                )}
                {{
                  asc: " 🔼",
                  desc: " 🔽",
                }[header.column.getIsSorted() as string] ?? null}
              </th>
            ))}
          </tr>
        ))}
      </thead>
      <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
        {table.getRowModel().rows.map((row) => (
          <tr key={row.id} className="hover:bg-gray-50 dark:hover:bg-gray-700">
            {row.getVisibleCells().map((cell) => (
              <td key={cell.id} className="px-6 py-4 dark:text-gray-300">
                {flexRender(cell.column.columnDef.cell, cell.getContext())}
              </td>
            ))}
          </tr>
        ))}
      </tbody>
    </table>
  </div>
);

const MobileTable = <T,>({ table }: { table: Table<T> }) => (
  <div className="md:hidden space-y-4 mt-6">
    {table.getRowModel().rows.map((row) => (
      <div
        key={row.id}
        className="bg-white dark:bg-gray-800 rounded-lg shadow p-4 space-y-3 border border-gray-200 dark:border-gray-700"
      >
        {row.getVisibleCells().map((cell) => (
          <div key={cell.id} className="flex justify-between items-center">
            <Text variant={"span"} status="secondary" size={"xs"}>
              {String(cell.column.columnDef.header)}
            </Text>
            <div className="dark:text-gray-300">
              {flexRender(cell.column.columnDef.cell, cell.getContext())}
            </div>
          </div>
        ))}
      </div>
    ))}
  </div>
);

export const GenericTable = <T,>({
  table,
  isLoading,
  error,
  isEmpty,
  customEmptyState,
  customLoadingState,
  customErrorState,
}: TableProps<T>) => {
  if (isLoading) {
    return customLoadingState || <LoadingState />;
  }

  if (error) {
    return customErrorState || <ErrorState />;
  }

  if (isEmpty) {
    return customEmptyState || <EmptyState />;
  }

  return (
    <>
      <DesktopTable table={table} />
      <MobileTable table={table} />
    </>
  );
};
